import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Modal,
  TextField,
  Typography,
  IconButton,
  Container,
  Fade,
  Chip,
  InputAdornment,
  useTheme,
  Grid,
  Skeleton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
  History as HistoryIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { productsApi } from '../api/products';
import { categoriesApi } from '../api/categories';

const SearchModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const searchInputRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [popularCategories, setPopularCategories] = useState([]);
  const [trendingSearches, setTrendingSearches] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Load recent searches from localStorage
  useEffect(() => {
    if (open) {
      try {
        const saved = localStorage.getItem('recentSearches');
        if (saved) {
          const parsed = JSON.parse(saved);
          setRecentSearches(Array.isArray(parsed) ? parsed : []);
        }
      } catch (error) {
        console.error('Error loading recent searches:', error);
        setRecentSearches([]);
      }
    }
  }, [open]);

  // Fetch categories from API when modal opens
  useEffect(() => {
    const fetchCategories = async () => {
      if (open && popularCategories.length === 0) {
        setCategoriesLoading(true);
        try {
          const result = await categoriesApi.getAll(20); // Get top 20 categories

          if (result && result.data && Array.isArray(result.data)) {
            // Generate colors for categories
            const categoryColors = [
              'var(--primary)', '#38003c', '#ee8707', '#024494', '#d20515',
              '#333', '#ff1493', '#ff6b6b', '#dc2626', '#4CAF50',
              '#2196F3', '#FF9800', '#9C27B0', '#00BCD4', '#8BC34A'
            ];

            // Transform categories for display (limit to first 9 for grid)
            const transformedCategories = result.data.slice(0, 9).map((category, index) => ({
              name: category.categoryName || category.name,
              slug: category.categoryName || category.name, // Use name as slug
              color: categoryColors[index % categoryColors.length],
              _id: category._id
            }));

            setPopularCategories(transformedCategories);

            // Set trending searches from category names (first 8)
            const trending = result.data.slice(0, 8).map(cat =>
              cat.categoryName || cat.name
            );
            setTrendingSearches(trending);
          }
        } catch (error) {
          console.error('Error fetching categories:', error);
          // Fallback to default categories if API fails
          setPopularCategories([
            { name: 'Harambe Stars', slug: 'Harambe Stars', color: 'var(--primary)' },
            { name: 'Premier League', slug: 'Premier League', color: '#38003c' },
            { name: 'La Liga', slug: 'La Liga', color: '#ee8707' },
            { name: 'Serie A', slug: 'Serie A', color: '#024494' },
            { name: 'Bundesliga', slug: 'Bundesliga', color: '#d20515' },
            { name: 'Tracksuits', slug: 'Tracksuits', color: '#333' },
            { name: 'Ladies', slug: 'Ladies', color: '#ff1493' },
            { name: 'Kids', slug: 'Kids', color: '#ff6b6b' },
            { name: 'Sale', slug: 'Sale', color: '#dc2626' },
          ]);

          setTrendingSearches([
            'Harambe Stars',
            'Premier League',
            'Arsenal Jersey',
            'Real Madrid',
            'Training Kit',
            'Kids Jersey',
            'Sale Items',
            'Custom Jersey',
          ]);
        } finally {
          setCategoriesLoading(false);
        }
      }
    };

    fetchCategories();
  }, [open, popularCategories.length]);

  // Focus search input when modal opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, onClose]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length > 1) {
        performSearch(searchQuery);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const performSearch = async (query) => {
    setLoading(true);
    try {
      // Use the getFilteredProducts API with search parameter
      const response = await productsApi.getFilteredProducts({
        search: query,
        limit: 6, // Limit to 6 results for preview
        page: 1
      });

      if (response && response.data) {
        setSearchResults(response.data);
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(true);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setShowResults(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    if (query.trim()) {
      // Save to recent searches
      const updatedSearches = [query.trim(), ...recentSearches.filter(s => s !== query.trim())].slice(0, 5);
      setRecentSearches(updatedSearches);

      try {
        localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
      } catch (error) {
        console.error('Error saving recent searches:', error);
      }

      // Navigate to products page with search query parameter
      navigate(`/products?search=${encodeURIComponent(query.trim())}`);
      onClose();
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`);
    onClose();
  };

  const handleCategoryClick = (slug) => {
    navigate(`/products?categoryName=${slug}`);
    onClose();
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    handleSearch(suggestion);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      sx={{
        zIndex: theme.zIndex.modal + 1,
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bgcolor: 'background.paper',
            maxHeight: '100vh',
            overflowY: 'auto',
            outline: 'none',
          }}
        >
          {/* Search Header */}
          <Box
            sx={{
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              position: 'sticky',
              top: 0,
              bgcolor: 'background.paper',
              zIndex: 1,
            }}
          >
            <Container maxWidth="lg" sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  ref={searchInputRef}
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch(searchQuery);
                    }
                  }}
                  placeholder="Search for teams, jerseys, or accessories..."
                  variant="standard"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery.length > 0 && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleSearch(searchQuery)}
                          sx={{
                            color: 'var(--primary)',
                            '&:hover': {
                              backgroundColor: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                            },
                          }}
                        >
                          <ArrowForwardIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                    disableUnderline: true,
                    sx: {
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      fontWeight: 300,
                      py: 1,
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderBottom: '2px solid transparent',
                      transition: 'border-color 0.3s ease',
                      '&:hover': {
                        borderBottom: '2px solid rgba(0, 0, 0, 0.1)',
                      },
                      '&.Mui-focused': {
                        borderBottom: '2px solid var(--primary)',
                      },
                    },
                  }}
                />
                <IconButton 
                  onClick={onClose}
                  sx={{ 
                    p: 1,
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Container>
          </Box>

          {/* Search Content */}
          <Container maxWidth="lg" sx={{ py: 4, minHeight: '60vh' }}>
            {/* Search Results */}
            {showResults && searchResults.length > 0 && (
              <Fade in={showResults}>
                <Box sx={{ mb: 6 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      letterSpacing: '0.08em',
                      mb: 3,
                    }}
                  >
                    TOP SUGGESTIONS
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {searchResults.map((product) => (
                      <Grid item xs={6} sm={4} md={2} key={product.id || product._id}>
                        <Box
                          onClick={() => handleProductClick(product.id || product._id)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              '& .product-image': {
                                transform: 'scale(1.05)',
                              },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              paddingTop: '125%',
                              position: 'relative',
                              overflow: 'hidden',
                              bgcolor: '#f5f5f5',
                              mb: 1,
                            }}
                          >
                            <Box
                              className="product-image"
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundImage: `url(${product.imageUrl || product.image || '/placeholder.png'})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 400,
                              fontSize: '0.875rem',
                              mb: 0.5,
                            }}
                          >
                            {product.name || product.productName}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.75rem',
                              mb: 0.5,
                            }}
                          >
                            {product.category || 'Uncategorized'}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.875rem',
                            }}
                          >
                            KSH {(product.price || product.discountedPrice || product.normalPrice || 0).toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* View All Results */}
                  <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography
                      component="span"
                      onClick={() => handleSearch(searchQuery)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      VIEW ALL RESULTS
                      <ArrowForwardIcon sx={{ fontSize: 16 }} />
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            )}

            {/* No Results */}
            {showResults && searchResults.length === 0 && searchQuery.length > 1 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  No results found for "{searchQuery}"
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try searching for something else or browse our categories
                </Typography>
              </Box>
            )}

            {/* Popular Categories */}
            {!showResults && (
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    letterSpacing: '0.08em',
                    mb: 3,
                  }}
                >
                  POPULAR CATEGORIES
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {categoriesLoading ? (
                    // Show skeleton loaders when loading
                    Array.from({ length: 9 }).map((_, index) => (
                      <Skeleton
                        key={index}
                        variant="rectangular"
                        width={100}
                        height={36}
                        sx={{ borderRadius: '20px' }}
                      />
                    ))
                  ) : (
                    popularCategories.map((category) => (
                      <Chip
                        key={category.slug}
                        label={category.name}
                        onClick={() => handleCategoryClick(category.slug)}
                        sx={{
                          borderRadius: '20px',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          border: `2px solid color-mix(in srgb, ${category.color} 12%, transparent)`,
                          backgroundColor: `color-mix(in srgb, ${category.color} 6%, transparent)`,
                          color: category.color,
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: category.color,
                            color: '#ffffff',
                            transform: 'scale(1.05)',
                          },
                        }}
                      />
                    ))
                  )}
                </Box>
              </Box>
            )}

            {/* Recent Searches */}
            {!showResults && recentSearches.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      letterSpacing: '0.08em',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <HistoryIcon sx={{ fontSize: 18 }} />
                    RECENT SEARCHES
                  </Typography>
                  <Typography
                    component="button"
                    onClick={clearRecentSearches}
                    sx={{
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      '&:hover': {
                        color: 'text.primary',
                      },
                    }}
                  >
                    Clear All
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {recentSearches.map((search, index) => (
                    <Chip
                      key={index}
                      label={search}
                      onClick={() => handleSuggestionClick(search)}
                      sx={{
                        borderRadius: '20px',
                        bgcolor: '#f5f5f5',
                        '&:hover': {
                          bgcolor: '#e0e0e0',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Trending Searches */}
            {!showResults && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    letterSpacing: '0.08em',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <TrendingIcon sx={{ fontSize: 18 }} />
                  TRENDING SEARCHES
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {categoriesLoading ? (
                    // Show skeleton loaders when loading
                    Array.from({ length: 8 }).map((_, index) => (
                      <Skeleton
                        key={index}
                        variant="rectangular"
                        width={90}
                        height={32}
                        sx={{ borderRadius: '20px' }}
                      />
                    ))
                  ) : (
                    trendingSearches.map((search, index) => (
                      <Chip
                        key={index}
                        label={search}
                        onClick={() => handleSuggestionClick(search)}
                        variant="outlined"
                        sx={{
                          borderRadius: '20px',
                          borderColor: 'rgba(0, 0, 0, 0.2)',
                          '&:hover': {
                            borderColor: 'var(--primary)',
                            bgcolor: 'rgba(46, 125, 50, 0.04)',
                          },
                        }}
                      />
                    ))
                  )}
                </Box>
              </Box>
            )}
          </Container>
        </Box>
      </Fade>
    </Modal>
  );
};

export default SearchModal;